import { useEffect, useMemo, useRef, useState } from 'react';
import type { HealthState, Resource } from '@itr/shared';
import { useRealtimeEvent } from './realtime';

/**
 * Keeps a fetched resource list live over the websocket.
 *
 * Health readings arrive constantly (one per check, per resource), so those
 * are patched into the rows in place rather than refetched. Anything that
 * changes the list's shape — a resource added, deleted, renamed, moved to
 * another site or parent, or a site of this client changing — triggers one
 * debounced refetch, so a bulk delete of twenty nodes costs one request.
 *
 * `scope` says which structural events concern this list: `all` for the
 * global lists (their filters are server-side, so refetch on any), or the
 * client whose resources are shown. Events about a row already in the list
 * always count, whatever the scope.
 */
export function useLiveResources(
  data: Resource[] | undefined,
  reload: () => void,
  scope: { all: true } | { customerId: string },
): Resource[] {
  // `escrow` rides in `attributes`, which a frame carries only partially — merged on its own below.
  const [overrides, setOverrides] = useState<Record<string, Partial<Resource> & { escrow?: unknown }>>({});
  const rows = data ?? [];
  const ids = useMemo(() => new Set(rows.map((r) => r.id)), [rows]);

  // A fresh fetch already carries the latest health; stale patches would
  // only mask it.
  useEffect(() => setOverrides({}), [data]);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const timerRef = useRef<number | null>(null);
  const scheduleReload = () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      reloadRef.current();
    }, 400);
  };
  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  const concerns = (customerId: string | null | undefined) => 'all' in scope || customerId === scope.customerId;

  useRealtimeEvent(['resources', 'sites'], (event) => {
    switch (event.type) {
      case 'resource.updated':
        if (!ids.has(event.resourceId)) return;
        setOverrides((current) => ({
          ...current,
          [event.resourceId]: {
            health: event.health as HealthState,
            statusMessage: event.statusMessage,
            lastCheckedAt: event.ts,
            ...('escrow' in (event.attributes ?? {}) ? { escrow: event.attributes.escrow } : {}),
          },
        }));
        return;
      case 'resource.created':
      case 'resource.changed':
      case 'resource.deleted':
        if (ids.has(event.resourceId) || concerns(event.customerId)) scheduleReload();
        return;
      case 'site.changed':
        if (concerns(event.customerId)) scheduleReload();
        return;
    }
  });

  return useMemo(
    () =>
      rows.map((r) => {
        const override = overrides[r.id];
        if (!override) return r;
        const { escrow, ...rest } = override;
        return { ...r, ...rest, ...('escrow' in override ? { attributes: { ...r.attributes, escrow } } : {}) };
      }),
    [rows, overrides],
  );
}
